import { callServer } from "../utils/request-util";
import environment from "environment";
import i18n from "../i18n";

// Tell the API which language to localize metadata (titles/descriptions) into.
const localeHeaders = () => ({ "x-api-locale": (i18n && i18n.language) || "en" });

// AnalyticsServer (ClickHouse analytics) — metadata-driven dashboards.
// All calls carry the user's Bearer token automatically (request-util) and are scoped
// server-side from the token's claims (Account / Territory / Depot) — never from the body.
const ANALYTICS_ROLES = [
  environment.USER_ROLES.supervisor,
  environment.USER_ROLES.deliveryManager,
  environment.USER_ROLES.depot,
  environment.USER_ROLES.supportOperator
];

const base = () => environment.analyticsAPI + "analytics/";

// The dashboards list is fetched by both the menu (availability check) and the analytics page on
// mount — dedupe so a single page load makes one request. Cache the in-flight/recent promise per
// locale for a short TTL; drop it on failure so a retry refetches.
let _dashboardsCache = null;
export function getAnalyticsDashboards() {
  const locale = (i18n && i18n.language) || "en";
  const now = Date.now();
  if (_dashboardsCache && _dashboardsCache.locale === locale && (now - _dashboardsCache.at) < 15000) {
    return _dashboardsCache.promise;
  }
  const promise = new Promise((resolve, reject) => {
    callServer({
      url: base() + "dashboards",
      method: "GET",
      headers: localeHeaders(),
      roles: ANALYTICS_ROLES
    }).then(response => resolve(response), error => reject(error && error.response));
  });
  _dashboardsCache = { locale, at: now, promise };
  promise.catch(() => { if (_dashboardsCache && _dashboardsCache.promise === promise) _dashboardsCache = null; });
  return promise;
}

export function getAnalyticsDashboard(dashboardId) {
  return new Promise((resolve, reject) => {
    callServer({
      url: base() + "dashboards/" + encodeURIComponent(dashboardId),
      method: "GET",
      headers: localeHeaders(),
      roles: ANALYTICS_ROLES
    }).then(response => resolve(response), error => reject(error && error.response));
  });
}

export function getAnalyticsMetrics() {
  return new Promise((resolve, reject) => {
    callServer({
      url: base() + "metrics",
      method: "GET",
      headers: localeHeaders(),
      roles: ANALYTICS_ROLES
    }).then(response => resolve(response), error => reject(error && error.response));
  });
}

export function getAnalyticsDimensions() {
  return new Promise((resolve, reject) => {
    callServer({
      url: base() + "dimensions",
      method: "GET",
      headers: localeHeaders(),
      roles: ANALYTICS_ROLES
    }).then(response => resolve(response), error => reject(error && error.response));
  });
}

// Freshness of a fact entity (max UpdatedAt of the account's rows) — shown as "data as of HH:MM";
// batch-recomputed facts (logistics) lag live pages by the recompute cadence.
export function getAnalyticsFreshness(entity) {
  return new Promise((resolve, reject) => {
    callServer({
      url: base() + "freshness/" + encodeURIComponent(entity),
      method: "GET",
      headers: localeHeaders(),
      roles: ANALYTICS_ROLES
    }).then(response => resolve(response), error => reject(error && error.response));
  });
}

// Option list for a dashboard filter whose values the portal doesn't know (an integration channel,
// an enum): the values the account's own facts actually carry over the range. Server-scoped like
// every other call here, so one city's user never sees another city's values.
export function getAnalyticsDimensionValues(dimension, from, to) {
  return new Promise((resolve, reject) => {
    callServer({
      // the dimension goes in the QUERY STRING: keys contain dots ("logistics.integration") and IIS
      // reads a dotted last path segment as a file request, 404-ing before OWIN can add CORS headers
      // — which surfaces in the browser as a CORS error rather than a 404
      url: base() + "dimension-values?dimension=" + encodeURIComponent(dimension)
        + "&from=" + encodeURIComponent(from) + "&to=" + encodeURIComponent(to),
      method: "GET",
      headers: localeHeaders(),
      roles: ANALYTICS_ROLES
    }).then(response => resolve(response), error => reject(error && error.response));
  });
}

// Live sources — a snapshot of "right now", NOT a ClickHouse query. A widget carrying `source`
// reads its rows from here instead of POST /query: there is no date range, no comparison and no
// metrics to send, and the scope (account / territory / depot / business) comes off the JWT exactly
// as it does for the semantic queries. The server caches the snapshot ~30s per scope, so a Refresh
// inside that window legitimately returns the same asOfUtc.
export function getAnalyticsLive(source) {
  return new Promise((resolve, reject) => {
    callServer({
      url: base() + "live/" + encodeURIComponent(source),
      method: "GET",
      headers: localeHeaders(),
      roles: ANALYTICS_ROLES
    }).then(response => resolve(response), error => reject(error && error.response));
  });
}

// Discovery of the available live-source keys. Tooling only — the dashboard definition already
// names the source a widget reads, so the page never needs this to render.
export function getAnalyticsLiveSources() {
  return new Promise((resolve, reject) => {
    callServer({
      url: base() + "live",
      method: "GET",
      headers: localeHeaders(),
      roles: ANALYTICS_ROLES
    }).then(response => resolve(response), error => reject(error && error.response));
  });
}

// The account's promos, running ones first. A row carries the offer's own settings plus what the
// server read off them — the goal, the status and how many orders have taken it; the mechanics are
// composed here, where the currency and the language are known.
export function getDiscounts() {
  return new Promise((resolve, reject) => {
    callServer({
      url: base() + "discounts",
      method: "GET",
      headers: localeHeaders(),
      roles: ANALYTICS_ROLES
    }).then(response => resolve(response), error => reject(error && error.response));
  });
}

// One promo against what its days usually do. Heavy on the server (it reads months of history to
// build the usual) and cached there per account-day, so a reload inside that window is free.
export function getDiscountPerformance(offerId) {
  return new Promise((resolve, reject) => {
    callServer({
      url: base() + "discounts/" + encodeURIComponent(offerId) + "/performance",
      method: "GET",
      headers: localeHeaders(),
      roles: ANALYTICS_ROLES
    }).then(response => resolve(response), error => reject(error && error.response));
  });
}

// The orders behind a promo report's "Orders" card, as a CSV file — one row each, with whether the
// promo was on it — so a figure can be checked against an export order by order.
export function getDiscountOrders(offerId) {
  return new Promise((resolve, reject) => {
    callServer({
      url: base() + "discounts/" + encodeURIComponent(offerId) + "/orders",
      method: "GET",
      responseType: "blob",
      roles: ANALYTICS_ROLES
    }).then(response => resolve(response), error => reject(error && error.response));
  });
}

// A dashboard's orders over its period as a CSV file (a dashboard with orderExport): one row each,
// with what the dashboard counts it by. The body is a widget's query — period, date basis, filters.
export function getDashboardOrders(dashboardId, request) {
  return new Promise((resolve, reject) => {
    callServer({
      url: base() + "dashboards/" + encodeURIComponent(dashboardId) + "/orders",
      method: "POST",
      data: request,
      headers: localeHeaders(),
      responseType: "blob",
      roles: ANALYTICS_ROLES
    }).then(response => resolve(response), error => reject(error && error.response));
  });
}

// "What else happened": notes people write on a promo. Not part of the report, which the server
// caches for minutes — a note just written has to be there on the next look.
function promoNotesUrl(offerId) {
  return base() + "discounts/" + encodeURIComponent(offerId) + "/notes";
}

export function getDiscountNotes(offerId) {
  return new Promise((resolve, reject) => {
    callServer({ url: promoNotesUrl(offerId), method: "GET", roles: ANALYTICS_ROLES })
      .then(response => resolve(response), error => reject(error && error.response));
  });
}

export function addDiscountNote(offerId, note) {
  return new Promise((resolve, reject) => {
    callServer({ url: promoNotesUrl(offerId), method: "POST", data: note, roles: ANALYTICS_ROLES })
      .then(response => resolve(response), error => reject(error && error.response));
  });
}

export function deleteDiscountNote(offerId, noteId) {
  return new Promise((resolve, reject) => {
    callServer({ url: promoNotesUrl(offerId) + "/" + encodeURIComponent(noteId), method: "DELETE", roles: ANALYTICS_ROLES })
      .then(response => resolve(response), error => reject(error && error.response));
  });
}

export function queryAnalytics(request) {
  return new Promise((resolve, reject) => {
    callServer({
      url: base() + "query",
      method: "POST",
      data: request,
      headers: localeHeaders(),
      roles: ANALYTICS_ROLES
    }).then(response => resolve(response), error => reject(error && error.response));
  });
}
